import { BaseItemResponse } from "../response/BaseItemResponse";
import { IGetListService } from "../../abstractions/services/actions/IGetListService";
import {
  IGetListYearGroupService,
  YearGroup,
} from "../../abstractions/services/actions/IGetListYearGroupService";
import { toDateOnly } from "../../utils/fechaUtils";

/** Servicio base para listados agrupados por año. No llama al API directamente:
 *  se apoya en un `IGetListService<T>` subyacente (que sincroniza y persiste,
 *  p. ej. un `BaseGetListService`), obtiene la lista plana y la agrupa por el
 *  año de `getFecha(item)`. La subclase solo aporta el servicio de lista y el
 *  campo fecha. */
export abstract class BaseGetYearGroupListService<T extends BaseItemResponse>
  implements IGetListYearGroupService<T>
{
  /** Servicio de lista plana subyacente que sincroniza y persiste los datos. */
  protected abstract get listService(): IGetListService<T>;

  /** Fecha del item (ISO, con o sin hora); define el año del grupo. */
  protected abstract getFecha(item: T): string;

  /** Obtiene la lista del servicio subyacente y la retorna agrupada por año
   *  (años descendente; items de cada año descendente por fecha). */
  async getListAsync(): Promise<YearGroup<T>[]> {
    const items = await this.listService.getListAsync();
    return this._group(items);
  }

  /** Agrupa por año usando `getFecha` (tolera ISO con hora). */
  private _group(items: T[]): YearGroup<T>[] {
    const byYear = new Map<number, T[]>();
    for (const item of items) {
      const year = Number(toDateOnly(this.getFecha(item)).slice(0, 4));
      if (!byYear.has(year)) byYear.set(year, []);
      byYear.get(year)!.push(item);
    }

    return [...byYear.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([year, list]) => ({
        year,
        items: [...list].sort((a, b) =>
          toDateOnly(this.getFecha(b)).localeCompare(
            toDateOnly(this.getFecha(a)),
          ),
        ),
      }));
  }
}
